//* 组件库
import { $message, type UiSimplebar } from "various-ui";
//* 组件属性
import type { ItemSearchProps, ItemSearchCandidate, ItemSearchCandidates } from "../index";

export const useComposable = (define: ItemSearchProps) => {
    //* 获取I18n
    const i18n = useI18n();

    //* 静态属性
    const vars: { keyword: string; timeout?: NodeJS.Timeout } = { keyword: "" };
    //* 响应式变量
    const refs = {
        visible: ref<boolean>(false),
        keyword: ref<string>(""),
        selects: ref<ItemSearchCandidate[]>([]),
        candidates: ref<ItemSearchCandidates[]>([]),
        controller: ref<AbortController>(),
    };

    //* 节点
    const nodes = {
        simplebar: ref<InstanceType<typeof UiSimplebar>>(),
    };

    //* 事件列表
    const methods = {
        //* 删除指定项
        delete: (index: number) => refs.selects.value.splice(index, 1),
        //* 选择内容
        select: (data: any) => {
            if (refs.selects.value.find((value) => data.id == value.id && data.type == value.type)) {
                $message.warning("当前内容已存在");
            } else {
                //* 更新数据
                refs.selects.value.push(data);
                //* 清空搜索
                refs.keyword.value = "";
                refs.visible.value = false;
            }
        },

        //* 取消展示函数
        cancel: (hidden?: boolean) => {
            //* 取消正在请求的接口
            refs.controller.value?.abort();
            refs.controller.value = undefined;
            //* 隐藏候选窗口
            refs.visible.value = !!hidden;
            //* 取消正在等待的定时器
            vars.timeout && clearTimeout(vars.timeout);
        },

        //* 搜索函数
        search: () => {
            //* 取消正在请求的接口
            refs.controller.value?.abort("取消");
            refs.controller.value = undefined;
            //* 数据初始化
            const keyword = refs.keyword.value.trim();
            if (!keyword) methods.cancel();
            else if (keyword == vars.keyword) methods.cancel(true);
            else {
                vars.timeout && clearTimeout(vars.timeout);
                vars.timeout = setTimeout(() => {
                    refs.controller.value = new AbortController();
                    api.frontend("/api/bio/variant/search", {
                        data: { keyword: keyword },
                        signal: refs.controller.value.signal,
                        finally: () => (refs.controller.value = undefined),
                        success: (response) => {
                            //* 检索关键词缓存
                            vars.keyword = keyword;
                            //* 更新数据
                            refs.candidates.value = response.sort((_v1: any, v2: any) => (v2.data.length == 0 ? -1 : 0)) || [];
                            refs.visible.value = true;
                            nextTick(() => {
                                const node = nodes.simplebar.value?.$el?.querySelector(".ui-simplebar-container");
                                if (node) {
                                    node.scrollTo({ top: 0 });
                                }
                            });
                        },
                    });
                }, 300);
            }
        },

        //* 更新搜索项
        update: (data: any) => {
            refs.selects.value = data.map((value: any) => ({
                id: value.id,
                name: value.name,
                type: value.type,
            }));
        },
    };

    //* 动态属性
    const binds = reactive({
        //* 已选内容动态属性
        container: computed(() => ({ "max-width": `${define.width}px` })),
        //* 输入框动态属性
        input: computed(() => ({
            placeholder: define.placeholder || i18n.t("Layout.InputTypeSearch", { type: "Keyword" }),
        })),
    });

    //* 处理事件
    const ons = {
        //* 输入框回调事件
        input: {
            blur: () => methods.cancel(),
            focus: () => methods.search(),
            input: () => methods.search(),
        },
    };

    return { ons, refs, nodes, binds, methods };
};
